import { useState } from 'react';

import { triggerBulkBiddingConfirmButtonClicked } from 'Services/analytics/events/bulkBiddingEvents/triggerBulkBiddingConfirmButtonClicked';

import { BULK_BIDDING_ERROR_TYPES } from '../error.helpers';
import type {
	ActionableVehicle,
	GetVehiclesForBulkBidding,
	UnactionableVehicle,
} from '../useFetchVehiclesForBulkBiddingCSV';

import { createBulkBidResult, createFailedBid, isFailedBid, logBidResults } from './useBulkBidding.helpers';
import type { BulkBidResult, FailedBid, SuccessfulBid, UseBulkBiddingParams } from './useBulkBidding.types';
import { useCreateBulkBidMutation } from './useCreateBulkBidMutation';
import { useRateLimitedQueue } from './useRateLimitedQueue';

export const useBulkBidding = ({ onAllSuccessful, signal }: UseBulkBiddingParams) => {
	const [isCreatingBids, setIsCreatingBids] = useState(false);
	const [bidsProcessedCount, setBidsProcessedCount] = useState(0);
	const [bidResults, setBidResults] = useState<BulkBidResult>();
	const { mutateAsync } = useCreateBulkBidMutation({ setBidsProcessedCount, signal });
	const queue = useRateLimitedQueue();

	const createSingleBid = async (actionableVehicle: ActionableVehicle) => {
		if (signal.aborted) return createFailedBid({ actionableVehicle, reason: BULK_BIDDING_ERROR_TYPES.ABORTED });

		return queue.add(async () => {
			try {
				return await mutateAsync({
					bidAmount: actionableVehicle.bidAmount,
					vehicle: actionableVehicle.vehicle,
				});
			} catch (error) {
				return createFailedBid({ actionableVehicle, reason: BULK_BIDDING_ERROR_TYPES.GENERAL_ERROR });
			}
		});
	};

	const handleBiddingResults = ({
		results,
		unactionableVehicles,
	}: {
		results: (SuccessfulBid | FailedBid)[];
		unactionableVehicles: UnactionableVehicle[];
	}) => {
		const hasFailedBids = unactionableVehicles.length > 0 || results.some(isFailedBid);
		const { failedBids, successfulBids } = hasFailedBids
			? createBulkBidResult({ results, unactionableVehicles })
			: { failedBids: [], successfulBids: results as SuccessfulBid[] };

		triggerBulkBiddingConfirmButtonClicked({
			failedBids,
			success: !hasFailedBids,
			successfulBids,
		});

		logBidResults({ failedBids, successfulBids });

		if (hasFailedBids) {
			setBidResults({ failedBids, successfulBids });
		} else {
			onAllSuccessful(successfulBids);
		}
	};

	const handleCreateBids = async (vehicles: GetVehiclesForBulkBidding) => {
		setIsCreatingBids(true);

		const results: (SuccessfulBid | FailedBid)[] = [];

		const actionablePromises = vehicles.actionable.map(async (actionableVehicle) => {
			const result = await createSingleBid(actionableVehicle);
			if (result) results.push(result);
		});

		await Promise.all(actionablePromises);

		if (signal.aborted) {
			const { failedBids, successfulBids } = createBulkBidResult({
				results,
				unactionableVehicles: vehicles.unactionable,
			});

			logBidResults({ failedBids, successfulBids });
			triggerBulkBiddingConfirmButtonClicked({ failedBids, success: failedBids.length === 0, successfulBids });
			setIsCreatingBids(false);
			return null;
		}

		handleBiddingResults({ results, unactionableVehicles: vehicles.unactionable });
		setIsCreatingBids(false);
		return null;
	};

	return {
		bidResults,
		bidsProcessedCount,
		handleCreateBids,
		isCreatingBids,
	};
};
